// Minnesotaänger Mini App — v1 message actions.
// New code (not from telegram-tt). Contract: CHAT_CONTRACT.md (POST /send,
// /reroll, /edit, /delete — all 202 Accepted, async). Mutations register a
// PendingOp and return immediately; polling (actions/api/polling.ts) keeps
// refreshing until the result is observable in the history.

import type { ActionReturnType, GlobalState } from '../../types';
import { miniappApi } from '../../../api/miniapp/client';
import { addActionHandler, getActions, getGlobal, setGlobal } from '../../index';
import { updateMiniapp } from '../../miniapp/update';
import { selectChatMessages, selectMessage } from '../../selectors';
import { getErrorMessage } from './initial';
import { addPendingOperation, hasPendingOperation, removePendingOperation } from './polling';

// How long to wait after a 202 before refreshing history, so the async
// worker has time to persist the change.
const ASYNC_REFRESH_DELAY_MS = 1000;

function withoutItem<T>(items: T[], item: T): T[] {
  return items.filter((x) => x !== item);
}

function omitKey<K extends string, V>(record: Record<K, V>, key: K): Record<K, V> {
  const { [key]: _removed, ...rest } = record;
  return rest as Record<K, V>;
}

function refreshAfterAsyncOp(chatId: string) {
  const actions = getActions();
  setTimeout(() => actions.loadHistory({ chatId }), ASYNC_REFRESH_DELAY_MS);
}

function maxEventId(global: GlobalState, chatId: string): number {
  return selectChatMessages(global, chatId).reduce((max, m) => Math.max(max, m.event_id), 0);
}

addActionHandler('sendMessage', async (global, actions, { chatId, text, replyToEventId }): Promise<void> => {
  const messageText = text.trim();
  if (!messageText) return;

  const conversation = global.miniapp.conversations.find((c) => c.chat_id === chatId);
  if (!conversation) return;

  // Per-chat sending flag guards against double-tap (CHAT_CONTRACT §5.2).
  if (global.miniapp.sendingChatIds.includes(chatId)) return;

  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    sendingChatIds: [...m.sendingChatIds, chatId],
    sendFailedByChatId: { ...m.sendFailedByChatId, [chatId]: null },
  })));

  // The tail baseline must be captured BEFORE the request: a fast worker can
  // deliver the reply while the HTTP request is still in flight, and the
  // completion predicate is event_id > tail (§5.3). Reading it after the
  // await would include the reply itself and the op would never complete.
  const tailEventIdAtSubmit = maxEventId(global, chatId);

  try {
    await miniappApi.send({
      chat_id: chatId,
      assistant_id: conversation.assistant_id,
      text: messageText,
      ...(replyToEventId ? { reply_to_event_id: replyToEventId } : {}),
    });

    // 202 Accepted = queued, not delivered. Leave sending state immediately
    // (CHAT_CONTRACT §3.3) and track completion via a pending op — the worker
    // takes 10–90s and the poll loop observes the result.
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      sendingChatIds: withoutItem(m.sendingChatIds, chatId),
      // Drop the reply preview only if it still references the target this
      // send used — the user may have switched replies while the request was
      // in flight (send-failure retention, §3.3).
      ...(replyToEventId !== undefined && m.replyingToEventIdByChatId[chatId] === replyToEventId
        ? { replyingToEventIdByChatId: omitKey(m.replyingToEventIdByChatId, chatId) }
        : {}),
      // Clear the composer only if the user hasn't typed something new while
      // the request was in flight (send-failure text retention, §3.3).
      ...(m.composerTextByChatId[chatId] === messageText
        ? { composerTextByChatId: omitKey(m.composerTextByChatId, chatId) }
        : {}),
    })));
    addPendingOperation(chatId, {
      kind: 'send',
      targetEventId: null,
      tailEventId: tailEventIdAtSubmit,
      startedAt: Date.now(),
    });
    refreshAfterAsyncOp(chatId);
  } catch (err) {
    // Failed before acceptance: text stays in the composer, error is visible
    // in the banner with a retry entry point (§3.3/§9).
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      sendingChatIds: withoutItem(m.sendingChatIds, chatId),
      sendFailedByChatId: { ...m.sendFailedByChatId, [chatId]: getErrorMessage(err) },
    })));
  }
});

addActionHandler('setComposerText', (global, actions, { chatId, text }): ActionReturnType => {
  return updateMiniapp(global, (m) => ({
    ...m,
    composerTextByChatId: text
      ? { ...m.composerTextByChatId, [chatId]: text }
      : omitKey(m.composerTextByChatId, chatId),
  }));
});

// v1 (fix-list E): reply targets are event_ids (l0 rowids).
addActionHandler('setReplyingToEvent', (global, actions, { chatId, eventId }): ActionReturnType => {
  return updateMiniapp(global, (m) => ({
    ...m,
    replyingToEventIdByChatId: { ...m.replyingToEventIdByChatId, [chatId]: eventId },
  }));
});

addActionHandler('clearReplyingToEvent', (global): ActionReturnType => {
  const { activeChatId } = global.miniapp;
  if (!activeChatId) return undefined;
  return updateMiniapp(global, (m) => ({
    ...m,
    replyingToEventIdByChatId: omitKey(m.replyingToEventIdByChatId, activeChatId),
  }));
});

addActionHandler('toggleMessageSelection', (global, actions, { chatId, messageId }): ActionReturnType => {
  const selected = global.miniapp.selectedMessageIdsByChatId[chatId] ?? [];
  const next = selected.includes(messageId)
    ? selected.filter((id) => id !== messageId)
    : [...selected, messageId];

  return updateMiniapp(global, (m) => ({
    ...m,
    selectedMessageIdsByChatId: { ...m.selectedMessageIdsByChatId, [chatId]: next },
  }));
});

addActionHandler('exitMessageSelectMode', (global): ActionReturnType => {
  if (Object.keys(global.miniapp.selectedMessageIdsByChatId).length === 0) return undefined;
  return updateMiniapp(global, (m) => ({
    ...m,
    selectedMessageIdsByChatId: {},
  }));
});

addActionHandler('copyMessage', async (global, actions, { chatId, messageId }): Promise<void> => {
  const message = selectMessage(global, chatId, Number(messageId));
  if (!message) return;

  try {
    await navigator.clipboard.writeText(message.text);
  } catch (err) {
    actions.showDialog({ title: '复制失败', message: '无法访问剪贴板' });
  }
});

addActionHandler('copySelectedMessages', async (global): Promise<void> => {
  const { activeChatId, selectedMessageIdsByChatId } = global.miniapp;
  if (!activeChatId) return;

  const selectedIds = selectedMessageIdsByChatId[activeChatId] ?? [];
  const texts = selectChatMessages(global, activeChatId)
    .filter((m) => selectedIds.includes(String(m.event_id)))
    .map((m) => m.text);

  if (texts.length === 0) return;

  try {
    await navigator.clipboard.writeText(texts.join('\n\n'));
  } catch (err) {
    getActions().showDialog({ title: '复制失败', message: '无法访问剪贴板' });
  }
});

// User-message reroll/edit/delete is blocked by the backend (no semantics for
// user messages in v1); the menu greys those items out and this guard keeps
// the frontend from inventing behavior.
function isUserMessage(global: GlobalState, chatId: string, eventId: number): boolean {
  return selectMessage(global, chatId, eventId)?.role === 'user';
}

// reroll/edit/delete share one shape: register a pending op (dedup guard,
// §5.2), fire the 202 request, and on failure drop the op and surface the
// error. On success the poll loop observes the target disappearing and
// refreshes history — the 410 case (already tombstoned/superseded) is a
// success signal and is handled by the completion predicate, not shown.
addActionHandler('rerollMessage', async (global, actions, { chatId, eventId }): Promise<void> => {
  if (isUserMessage(global, chatId, eventId)) return;
  if (hasPendingOperation(global, chatId, eventId)) return;

  addPendingOperation(chatId, {
    kind: 'reroll',
    targetEventId: eventId,
    tailEventId: maxEventId(global, chatId),
    startedAt: Date.now(),
  });

  try {
    await miniappApi.reroll({ chat_id: chatId, event_id: eventId });
    refreshAfterAsyncOp(chatId);
  } catch (err) {
    removePendingOperation(chatId, eventId, 'reroll');
    actions.showDialog({ title: '重新生成失败', message: getErrorMessage(err) });
  }
});

addActionHandler('editMessage', async (global, actions, { chatId, eventId, text }): Promise<void> => {
  const messageText = text.trim();
  if (!messageText) return;
  if (isUserMessage(global, chatId, eventId)) return;
  if (hasPendingOperation(global, chatId, eventId)) return;

  addPendingOperation(chatId, {
    kind: 'edit',
    targetEventId: eventId,
    tailEventId: maxEventId(global, chatId),
    startedAt: Date.now(),
  });

  try {
    await miniappApi.edit({ chat_id: chatId, event_id: eventId, text: messageText });
    refreshAfterAsyncOp(chatId);
  } catch (err) {
    removePendingOperation(chatId, eventId, 'edit');
    actions.showDialog({ title: '编辑失败', message: getErrorMessage(err) });
  }
});

addActionHandler('deleteMessage', async (global, actions, { chatId, eventId }): Promise<void> => {
  if (isUserMessage(global, chatId, eventId)) return;
  if (hasPendingOperation(global, chatId, eventId)) return;

  addPendingOperation(chatId, {
    kind: 'delete',
    targetEventId: eventId,
    tailEventId: maxEventId(global, chatId),
    startedAt: Date.now(),
  });

  try {
    await miniappApi.delete({ chat_id: chatId, event_id: eventId });
    refreshAfterAsyncOp(chatId);
  } catch (err) {
    removePendingOperation(chatId, eventId, 'delete');
    actions.showDialog({ title: '删除失败', message: getErrorMessage(err) });
  }
});
